import { Nullable } from "../Extensions";
import { ArrayHelper } from "../Helpers/ArrayHelper";
import { ServiceManager } from "../ServiceManager";
import { nameof } from "./DotNet/Extensions";
import { LookupItem } from "./Schema/Entities/LookupItem.Generated";
import { WebApiClientService } from "./WebApiClientService";

interface ILookupItemDto
{
    id: string;
    deletedGuid: boolean;
    description: string;
    isLinkedToEnum: boolean;
    order: number;
}

export class LookupItemCache
{
    private static _byTypeMap = new Map<Nullable<string>, ILookupItemDto[]>();
    private static _hasLoadedTopLevelLookupItems: boolean = false;
    private static _idDescriptionMap = new Map<string, string>();
    private static _topLevelLookupItems: ILookupItemDto[];

    public static async getDescriptionAsync(id: Nullable<string> | undefined): Promise<Nullable<string>>
    {
        if (!id)
            return null;

        let
            value = this._idDescriptionMap.get(id);

        if (value !== undefined)
            return value;

        const
            lookupItems =
                await
                    ServiceManager.getRequired(WebApiClientService)
                        .createEntitySearchRequest("LookupItems", { columns: [nameof(LookupItem, "id"), nameof(LookupItem, "description")], filter: `${nameof(LookupItem, "id")} eq ${id}` }, { "RAD-Option-Include-Soft-Deleted": true })
                        .executeAsync() as any[],
            lookupItem = lookupItems && lookupItems.length ? lookupItems[0] : null;

        if (!lookupItem)
            return null;

        const
            description = lookupItem[nameof(LookupItem, "description")];

        if (!description)
            return null;

        this._idDescriptionMap.set(id, description);

        return description;
    }

    public static async getByTypeAsync(type: Nullable<string>, requiredId: Nullable<string>, orderBy: Nullable<"description" | "order">)
    {
        const
            columns = [
                nameof(LookupItem, "id"),
                nameof(LookupItem, "deletedGuid"),
                nameof(LookupItem, "description"),
                nameof(LookupItem, "isLinkedToEnum"),
                nameof(LookupItem, "order"),
            ];

        if (!this._hasLoadedTopLevelLookupItems)
        {
            this._topLevelLookupItems =
                await
                    ServiceManager.getRequired(WebApiClientService)
                        .createEntitySearchRequest(
                            "LookupItems",
                            {
                                columns,
                                filter: `${nameof(LookupItem, "typeLookupItemId")} eq null`,
                            },
                            {
                                "RAD-Option-Include-Soft-Deleted": true
                            },
                        )
                        .executeAsync() as ILookupItemDto[];
            this._hasLoadedTopLevelLookupItems = true;
        }

        const
            typeLookupItem = type !== null ? ArrayHelper.firstOrDefault(this._topLevelLookupItems.filter(item => item.description === type)) : null;

        orderBy ??= typeLookupItem?.isLinkedToEnum ? "order" : "description";

        let
            lookupItems: ILookupItemDto[] | undefined

        if (!type)
            lookupItems = this._topLevelLookupItems;
        else
        {
            lookupItems = this._byTypeMap.get(type);

            if (!lookupItems)
            {
                lookupItems =
                    await
                        ServiceManager.getRequired(WebApiClientService)
                            .createEntitySearchRequest(
                                "LookupItems",
                                {
                                    columns,
                                    filter: `${nameof(LookupItem, "typeLookupItem")}/${nameof(LookupItem, "description")} eq '${type}'`,
                                },
                                {
                                    "RAD-Option-Include-Soft-Deleted": true
                                },
                            )
                            .executeAsync() as ILookupItemDto[];

                this._byTypeMap.set(type, lookupItems);
            }
        }

        for (const lookupItem of lookupItems)
            this._idDescriptionMap.set(lookupItem.id, lookupItem.description);

        return (
            ArrayHelper.orderBy(
                lookupItems
                    .filter(
                        item =>
                            !item.deletedGuid
                            || requiredId && item.id === requiredId
                    ),
                item =>
                {
                    switch (orderBy)
                    {
                        case "description":
                            return item.description;
                        case "order":
                            return item.order;
                        default:
                            throw new Error(`Order by "${orderBy}" is not supported`);
                    }
                }
            )
        );
    }
}